import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../services/api";
import NotificationToolbarBell from "../shared/NotificationToolbarBell";
import VoiceGuidanceToolbar from "../shared/VoiceGuidanceToolbar";
import "./CustomerAppHeader.css";

function customerDisplayName(profile) {
  const first = String(profile?.firstName || profile?.first_name || "").trim();
  const last = String(profile?.lastName || profile?.last_name || "").trim();
  const combined = `${first} ${last}`.trim();
  const named = (
    combined ||
    String(profile?.name || profile?.fullName || profile?.username || profile?.mobile || "").trim()
  );
  if (named) return named;
  return api.getToken?.() ? "there" : "Guest";
}

function greetingWord(now = new Date()) {
  const hour = Number(
    new Intl.DateTimeFormat("en-GB", {
      hour: "2-digit",
      hour12: false,
      timeZone: "Asia/Kolkata",
    }).format(now)
  );
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

function subtitleForPath(pathname) {
  if (pathname.startsWith("/marketplace") || pathname.startsWith("/book-care")) {
    return "Find the right caregiver for your family.";
  }
  if (pathname.startsWith("/my-bookings")) return "See and manage your care bookings.";
  if (pathname.startsWith("/care-team")) return "Your assigned caregivers and care team.";
  if (pathname.startsWith("/elder-profile")) return "Patients in your family account.";
  if (pathname.startsWith("/care-logs")) return "Daily care notes and health updates.";
  if (pathname.startsWith("/subscription")) {
    return "Manage your care passes and session packages.";
  }
  if (pathname.startsWith("/payment-history")) return "View your payments, invoices and transaction history.";
  if (pathname.startsWith("/my-care-manager")) {
    return "Your care manager is here to coordinate and oversee the care of your loved one.";
  }
  if (pathname.startsWith("/support")) {
    return "We're here to help you. Find answers, track your queries or contact our support team.";
  }
  if (pathname.startsWith("/settings")) return "Manage account preferences and notifications.";
  if (pathname.startsWith("/payments")) {
    return "View your purchased passes, packages, subscriptions and top-ups.";
  }
  if (pathname.startsWith("/messenger")) return "Messages with your care team.";
  if (pathname.startsWith("/notifications")) return "Alerts and updates for your family.";
  if (pathname.startsWith("/profile")) return "Your family account details.";
  if (pathname.startsWith("/call-history")) return "Your care call history.";
  return "Here's your family care overview.";
}

function searchPathForLocation(pathname, query) {
  const q = query.trim();
  const encoded = q ? `?q=${encodeURIComponent(q)}` : "";
  if (pathname.startsWith("/my-bookings")) return `/my-bookings${encoded}`;
  if (pathname.startsWith("/care-team")) return `/care-team${encoded}`;
  if (pathname.startsWith("/elder-profile")) return `/elder-profile${encoded}`;
  if (pathname.startsWith("/care-logs")) return `/care-logs${encoded}`;
  if (pathname.startsWith("/payment-history")) return `/payment-history${encoded}`;
  if (pathname.startsWith("/payments")) return `/payments${encoded}`;
  if (pathname.startsWith("/support")) return `/support${encoded}`;
  if (pathname.startsWith("/marketplace")) return `/marketplace${encoded}`;
  return q ? `/marketplace${encoded}` : "/marketplace";
}

export default function CustomerAppHeader({ onMenuToggle = null }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(() => api.getStoredCustomerProfile?.() || null);
  const [headerQuery, setHeaderQuery] = useState(() => new URLSearchParams(location.search).get("q") || "");

  const displayName = useMemo(() => customerDisplayName(profile), [profile]);
  const isSupport = location.pathname.startsWith("/support");
  const isPayments = location.pathname.startsWith("/payment-history");
  const greeting = `${greetingWord()}, ${displayName}`;
  const heading = isSupport ? "Support Center" : isPayments ? "Payments" : greeting;
  const subtitle = subtitleForPath(location.pathname);

  useEffect(() => {
    setHeaderQuery(new URLSearchParams(location.search).get("q") || "");
  }, [location.search, location.pathname]);

  useEffect(() => {
    setProfile(api.getStoredCustomerProfile?.() || null);
    function onUpdate(event) {
      setProfile(event?.detail || api.getStoredCustomerProfile?.() || null);
    }
    window.addEventListener(api.customerProfileUpdatedEvent, onUpdate);
    return () => window.removeEventListener(api.customerProfileUpdatedEvent, onUpdate);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const cached = api.getStoredCustomerProfile?.();
    const hasName = Boolean(
      String(cached?.firstName || "").trim() ||
        String(cached?.fullName || cached?.name || "").trim() ||
        String(cached?.mobile || "").trim()
    );
    if (hasName || !api.getToken?.()) return undefined;
    if (api.getAuthRoleFromToken?.() !== "CUSTOMER") return undefined;

    api
      .getCustomerProfile?.()
      .then((res) => {
        if (cancelled) return;
        const next = res?.user || res?.profile || res || null;
        if (!next) return;
        const stored = api.setStoredCustomerProfile?.(next);
        setProfile(stored || api.getStoredCustomerProfile?.() || next);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  function handleSearch(event) {
    event.preventDefault();
    navigate(searchPathForLocation(location.pathname, headerQuery));
  }

  return (
    <header className="customer-layout__toolbar customer-app-header">
      {onMenuToggle ? (
        <button
          type="button"
          className="layout-hamburger layout-hamburger--toolbar"
          aria-label="Open menu"
          onClick={onMenuToggle}
        >
          ☰
        </button>
      ) : null}

      <div className="customer-app-header__greeting">
        <h1>{heading}</h1>
        <p>{subtitle}</p>
      </div>

      <form className="customer-app-header__search" onSubmit={handleSearch} role="search">
        <span className="customer-app-header__search-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8">
            <circle cx="11" cy="11" r="6" />
            <path d="M20 20l-4.2-4.2" />
          </svg>
        </span>
        <input
          type="search"
          value={headerQuery}
          onChange={(event) => setHeaderQuery(event.target.value)}
          placeholder={
            isSupport
              ? "Search for help articles, topics..."
              : isPayments
                ? "Search payments, invoices, bookings..."
                : location.pathname.startsWith("/my-bookings")
                  ? "Search bookings, caregivers..."
                : location.pathname.startsWith("/payments")
                  ? "Search by pass, package, subscription or invoice ID..."
                  : "Search anything..."
          }
          aria-label={
            isSupport
              ? "Search for help articles, topics"
              : isPayments
                ? "Search payments, invoices, bookings"
                : location.pathname.startsWith("/payments")
                  ? "Search by pass, package, subscription or invoice ID"
                  : "Search anything"
          }
        />
      </form>

      <div className="customer-app-header__actions">
        {isSupport ? (
          <button
            type="button"
            className="customer-app-header__help"
            aria-label="Help articles"
            onClick={() => {
              if (location.pathname.startsWith("/support")) {
                document.getElementById("help-articles")?.scrollIntoView({ behavior: "smooth", block: "start" });
                return;
              }
              navigate("/support#help-articles");
            }}
          >
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
              <circle cx="12" cy="12" r="9" />
              <path d="M9.5 9.5a2.5 2.5 0 014.8.8c0 1.7-2.3 2.1-2.3 3.7" />
              <circle cx="12" cy="17" r="0.8" fill="currentColor" stroke="none" />
            </svg>
          </button>
        ) : null}
        <button type="button" className="customer-app-header__messages" onClick={() => navigate("/messenger")}>
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
            <path d="M21 15a4 4 0 01-4 4H8l-5 3V7a4 4 0 014-4h10a4 4 0 014 4z" />
          </svg>
          Messages
        </button>
        <VoiceGuidanceToolbar variant="toolbar" />
        <div className="customer-app-header__notify">
          <NotificationToolbarBell />
          <span>Notifications</span>
        </div>
      </div>
    </header>
  );
}
